import React, { useState } from 'react';
import { AiFillStar } from 'react-icons/ai';
import { cx } from './hooks';

/**
 * Star rating. Read-only when `disabled` (or when no onChange is given),
 * otherwise clicking a star calls onChange(value); clicking the current value clears it.
 * allowHalf shows half stars for values like 4.5 and lets the left half of a star pick x.5.
 */
const Rating = ({
  value,
  defaultValue = 0,
  onChange,
  disabled = false,
  allowHalf = false,
  allowClear = true,
  count = 5,
  size,
  className,
  style,
  id,
}) => {
  const [inner, setInner] = useState(defaultValue);
  const [hover, setHover] = useState(null);
  const current = Number(value ?? inner) || 0;
  const interactive = !disabled && (onChange || value === undefined);
  const shown = hover ?? current;

  const select = star => {
    const next = allowClear && star === current ? 0 : star;
    if (value === undefined) setInner(next);
    onChange?.(next);
  };

  // With allowHalf, the left half of a star picks x.5 (mouse only; keyboard picks whole stars).
  const pointerValue = (e, i) => {
    if (!allowHalf || !e.clientX) return i + 1;
    const rect = e.currentTarget.getBoundingClientRect();
    return e.clientX - rect.left < rect.width / 2 ? i + 0.5 : i + 1;
  };

  const label = `${current} out of ${count} stars`;

  return (
    <span
      id={id}
      className={cx('rate', size === 'small' && 'rate-sm', interactive && 'is-interactive', className)}
      style={style}
      role={interactive ? 'radiogroup' : 'img'}
      aria-label={label}
      onMouseLeave={() => setHover(null)}
    >
      {Array.from({ length: count }, (_, i) => {
        const full = i + 1 <= shown;
        const half = !full && allowHalf && i + 0.5 <= shown;
        const star = (
          <>
            <AiFillStar />
            {half && (
              <span className="rate-star-half">
                <AiFillStar />
              </span>
            )}
          </>
        );

        if (!interactive) {
          return (
            <span key={i} className={cx('rate-star', full && 'is-full')} aria-hidden="true">
              {star}
            </span>
          );
        }

        return (
          <button
            key={i}
            type="button"
            className={cx('rate-star', full && 'is-full')}
            role="radio"
            aria-checked={i + 1 === current || i + 0.5 === current}
            aria-label={`${i + 1} star${i ? 's' : ''}`}
            onMouseMove={e => setHover(pointerValue(e, i))}
            onFocus={() => setHover(i + 1)}
            onBlur={() => setHover(null)}
            onClick={e => select(pointerValue(e, i))}
          >
            {star}
          </button>
        );
      })}
    </span>
  );
};

export default Rating;
